import React, { type ReactNode } from "react";

import PlayPauseButton from "./PlayPauseButton";
import PlaybackClock from "./PlaybackClock";
import AudioPlayerBase, { type IProps as IAudioPlayerBaseProps, type IState } from "./AudioPlayerBase";
import LegacySeekBar from "./LegacySeekBar";
import PlaybackWaveform from "./PlaybackWaveform";
import { PlaybackState } from "../../../audio/Playback";
import SettingsStore from "../../../settings/SettingsStore";
import { SettingLevel } from "../../../settings/SettingLevel";

export enum PlaybackLayout {
    /**
     * Clock on the left side of a waveform, without seek bar.
     */
    Composer,

    /**
     * Clock on the right side of a waveform, with an added seek bar.
     */
    Timeline,
}

interface IProps extends IAudioPlayerBaseProps {
    layout?: PlaybackLayout; // Defaults to Timeline layout
}

const PLAYBACK_SPEEDS = [1, 1.5, 2];

interface RecordingPlaybackState extends IState {
    playbackSpeed: number;
}

export default class RecordingPlayback extends AudioPlayerBase<IProps> {
    private speedWatcherRef?: string;

    public state: RecordingPlaybackState = {
        playbackPhase: this.props.playback.currentState,
        playbackSpeed: SettingsStore.getValue("audioPlaybackSpeed") ?? 1,
    };

    public componentDidMount(): void {
        super.componentDidMount();
        // Apply the persisted speed immediately
        this.props.playback.setPlaybackRate(this.state.playbackSpeed);
        // Watch for speed changes from other instances
        this.speedWatcherRef = SettingsStore.watchSetting(
            "audioPlaybackSpeed", null, (...[, , , value]) => {
                const speed = (value as number) || 1;
                this.props.playback.setPlaybackRate(speed);
                this.setState({ playbackSpeed: speed } as RecordingPlaybackState);
            },
        );
    }

    public componentWillUnmount(): void {
        if (this.speedWatcherRef) {
            SettingsStore.unwatchSetting(this.speedWatcherRef);
        }
    }

    private cycleSpeed = (): void => {
        const currentIdx = PLAYBACK_SPEEDS.indexOf(this.state.playbackSpeed);
        const nextSpeed = PLAYBACK_SPEEDS[(currentIdx + 1) % PLAYBACK_SPEEDS.length];
        this.props.playback.setPlaybackRate(nextSpeed);
        this.setState({ playbackSpeed: nextSpeed } as RecordingPlaybackState);
        SettingsStore.setValue("audioPlaybackSpeed", null, SettingLevel.DEVICE, nextSpeed);
    };

    private renderComposerLook(): ReactNode {
        return (
            <>
                <PlaybackClock playback={this.props.playback} />
                <PlaybackWaveform playback={this.props.playback} />
            </>
        );
    }

    private renderTimelineLook(): ReactNode {
        return (
            <>
                <div className="mx_RecordingPlayback_timelineLayoutMiddle">
                    <PlaybackWaveform playback={this.props.playback} />
                    <LegacySeekBar
                        playback={this.props.playback}
                        tabIndex={0} // allow keyboard users to fall into the seek bar
                        disabled={this.state.playbackPhase === PlaybackState.Decoding}
                        ref={this.seekRef}
                    />
                </div>
                <PlaybackClock playback={this.props.playback} />
            </>
        );
    }

    protected renderComponent(): ReactNode {
        let body: ReactNode;
        switch (this.props.layout) {
            case PlaybackLayout.Composer:
                body = this.renderComposerLook();
                break;
            case PlaybackLayout.Timeline: // default is timeline, fall through.
            default:
                body = this.renderTimelineLook();
                break;
        }

        // Mostro sempre il pulsante di cambio velocità, anche in composer,
        // perché è una funzionalità che potrebbe essere utile anche lì.
        // Se poi vogliamo nasconderlo in composer, basta riaggiungere la condizione isTimeline.
        // const isTimeline = this.props.layout !== PlaybackLayout.Composer;

        return (
            <div className="mx_MediaBody mx_VoiceMessagePrimaryContainer" onKeyDown={this.onKeyDown}>
                <PlayPauseButton
                    playback={this.props.playback}
                    playbackPhase={this.state.playbackPhase}
                    ref={this.playPauseRef}
                />
                <div className="mx_RecordingPlayback_timelineRow">
                    {body}
                    {/* {isTimeline && ( */}
                        <button
                            className="mx_PlaybackSpeedButton"
                            onClick={this.cycleSpeed}
                            aria-label={`Playback speed ${this.state.playbackSpeed}x`}
                        >
                            {this.state.playbackSpeed}x
                        </button>
                    {/* )} */}
                </div>
            </div>
        );
    }
}
